import { OPERATION_MODES } from "../../devices/constants";
import {
    IconAcUnit,
    IconBeachAccess,
    IconBrightness2,
    IconBusinessCenter,
    IconCake,
    IconCelebration,
    IconCloud,
    IconCrueltyFree,
    IconDarkMode,
    IconDirectionsCar,
    IconFlag,
    IconHome,
    IconIcecream,
    IconLooks,
    IconPalette,
    IconPartlyCloudyNight,
    IconRedeem,
    IconSailing,
    IconSentimentDissatisfied,
    IconSentimentSatisfied,
    IconStar,
    IconThunderstorm,
    IconTsunami,
    IconUmbrella,
    IconWbSunny,
    IconWbTwilight,
} from "../../layout/components/Icons";

export const TEMPERATURE_SETTINGS = Object.freeze({
    LOWER_LIMIT_TEMPERATURE: {
        key: "LOWER_LIMIT_TEMPERATURE",
        min: 6,
        max: 30,
    },
    UPPER_LIMIT_TEMPERATURE: {
        key: "UPPER_LIMIT_TEMPERATURE",
        min: 5,
        max: 29,
    },
    UNOCCUPIED_TEMPERATURE: {
        key: "UNOCCUPIED_TEMPERATURE",
        min: 5,
        max: 30,
    },
    OCCUPIED_TEMPERATURE: {
        key: "OCCUPIED_TEMPERATURE",
        min: 5,
        max: 30,
    },
    OFFSET_TEMPERATURE: {
        key: "OFFSET_TEMPERATURE",
        min: -5,
        max: 5,
    },
    HEATING_PREFERENCE_TEMPERATURE: {
        key: "HEATING_PREFERENCE_TEMPERATURE",
        min: 0, //not used
        max: 0, // not used
    },
} as const);

export const HEATING_PREFERENCE_SETTINGS = Object.freeze({
    COMFORT: "COMFORT",
    ENERGY_SAVING: "ENERGY_SAVING",
} as const);

export const EVENTS = Object.freeze({
    EVENT_OCCUPANCY_RESERVATION_STATUS_CHANGE:
        "EVENT_OCCUPANCY_RESERVATION_STATUS_CHANGE",
    EVENT_OCCUPANCY_STATUS_CHANGE: "EVENT_OCCUPANCY_STATUS_CHANGE",
    EVENT_DEVICE_MEASURED_TEMPERATURE_CHANGE:
        "EVENT_DEVICE_MEASURED_TEMPERATURE_CHANGE",
    EVENT_DEVICE_DESIRED_TEMPERATURE_CHANGE_USER:
        "EVENT_DEVICE_DESIRED_TEMPERATURE_CHANGE_USER",
    EVENT_DEVICE_DESIRED_TEMPERATURE_CHANGE_SOFTWARE:
        "EVENT_DEVICE_DESIRED_TEMPERATURE_CHANGE_SOFTWARE",
    EVENT_DEVICE_WINDOW_CONTACT_CHANGE: "EVENT_DEVICE_WINDOW_CONTACT_CHANGE",
    EVENT_TIME: "EVENT_TIME",
    EVENT_SPACE_STATUS_CHANGE: "EVENT_SPACE_STATUS_CHANGE",
});

export const EVENTS_TEMPERATURE = [
    EVENTS.EVENT_DEVICE_DESIRED_TEMPERATURE_CHANGE_SOFTWARE,
    EVENTS.EVENT_DEVICE_DESIRED_TEMPERATURE_CHANGE_USER,
    EVENTS.EVENT_DEVICE_MEASURED_TEMPERATURE_CHANGE,
];

export const CONDITIONS = Object.freeze({
    CONDITION_OCCUPANCY_STATUS: "CONDITION_OCCUPANCY_STATUS",
    CONDITION_OCCUPANCY_RESERVATION_STATUS:
        "CONDITION_OCCUPANCY_RESERVATION_STATUS",
    CONDITION_DEVICE_WINDOW_CONTACT_STATUS:
        "CONDITION_DEVICE_WINDOW_CONTACT_STATUS",
    CONDITION_SPACE_STATUS: "CONDITION_SPACE_STATUS",
});

export const ACTIONS = Object.freeze({
    ACTION_SET_DESIRED_TEMPERATURE: "ACTION_SET_DESIRED_TEMPERATURE",
    ACTION_OFFSET_SET_DESIRED_TEMPERATURE:
        "ACTION_OFFSET_SET_DESIRED_TEMPERATURE",
    ACTION_TURN_OFF: "ACTION_TURN_OFF",
    ACTION_SET_RELAY_STATUS: "ACTION_SET_RELAY_STATUS",
});

export const OPERATIONS = Object.freeze({
    DELTA: "DELTA",
    EQUAL: "EQUAL",
    ABOVE: "ABOVE",
    BELOW: "BELOW",
    NOT_EQUAL: "NOT_EQUAL",
    BEFORE: "BEFORE",
    BETWEEN: "BETWEEN",
});

export const RULE_VALUES = Object.freeze({
    RESERVED: "RESERVED",
    CANCELED: "CANCELED",
    IN_HOUSE: "IN_HOUSE",
    WAITLIST: "WAITLIST",
    NO_SHOW: "NO_SHOW",
    CHECKED_OUT: "CHECKED_OUT",
    UNKNOWN: "UNKNOWN",
    OCCUPIED: "OCCUPIED",
    UNOCCUPIED: "UNOCCUPIED",
    OPEN: "OPEN",
    CLOSED: "CLOSED",
    ON: "ON",
    OFF: "OFF",
});

export const EVENT_TYPES = Object.freeze({
    ENUM: "ENUM",
    MULTI_ENUM: "MULTI_ENUM",
    TEMPERATURE: "TEMPERATURE",
    TIME: "TIME",
    OFFSET: "OFFSET",
    INTEGER: "INTEGER",
    BOOLEAN: "BOOLEAN",
});

export const TRIGGER = Object.freeze({
    MANUAL: "MANUAL",
    GUEST: "GUEST",
    UI: "UI",
    RULE: "RULE",
    MODE: "MODE",
    SYSTEM: "SYSTEM",
});

export const MODE_TYPES = Object.freeze({
    BEACH_ACCESS: "BEACH_ACCESS",
    CELEBRATION: "CELEBRATION",
    AC_UNIT: "AC_UNIT",
    BRIGHTNESS_2: "BRIGHTNESS_2",
    BUSINESS_CENTER: "BUSINESS_CENTER",
    CAKE: "CAKE",
    CLOUD: "CLOUD",
    CRUELTY_FREE: "CRUELTY_FREE",
    DARK_MODE: "DARK_MODE",
    DIRECTION_CAR: "DIRECTION_CAR",
    FLAG: "FLAG",
    HOME: "HOME",
    ICECREAM: "ICECREAM",
    LOOKS: "LOOKS",
    PALETTE: "PALETTE",
    PARTLY_CLOUDY_NIGHT: "PARTLY_CLOUDY_NIGHT",
    REDEEM: "REDEEM",
    SAILING: "SAILING",
    SENTIMENT_DISSATISFIED: "SENTIMENT_DISSATISFIED",
    SENTIMENT_SATISFIED: "SENTIMENT_SATISFIED",
    STAR: "STAR",
    THUNDERSTORM: "THUNDERSTORM",
    TSUNAMI: "TSUNAMI",
    UMBRELLA: "UMBRELLA",
    WB_SUNNY: "WB_SUNNY",
    WB_TWILIGHT: "WB_TWILIGHT",
} as const);

export const MODE_ICONS = Object.freeze({
    [MODE_TYPES.AC_UNIT]: <IconAcUnit key="ac_unit" />,
    [MODE_TYPES.WB_SUNNY]: <IconWbSunny key="wb_sunny" />,
    [MODE_TYPES.WB_TWILIGHT]: <IconWbTwilight key="wb_twilight" />,
    [MODE_TYPES.STAR]: <IconStar key="star" />,
    [MODE_TYPES.BRIGHTNESS_2]: <IconBrightness2 key="brightness_2" />,
    [MODE_TYPES.DARK_MODE]: <IconDarkMode key="dark_mode" />,
    [MODE_TYPES.PARTLY_CLOUDY_NIGHT]: (
        <IconPartlyCloudyNight key="partly_cloudy_night" />
    ),
    [MODE_TYPES.CLOUD]: <IconCloud key="cloud" />,
    [MODE_TYPES.THUNDERSTORM]: <IconThunderstorm key="thunderstorm" />,
    [MODE_TYPES.BEACH_ACCESS]: <IconBeachAccess key="beach_access" />,
    [MODE_TYPES.UMBRELLA]: <IconUmbrella key="umbrella" />,
    [MODE_TYPES.HOME]: <IconHome key="home" />,
    [MODE_TYPES.SENTIMENT_DISSATISFIED]: (
        <IconSentimentDissatisfied key="sentiment_dissatisfied" />
    ),
    [MODE_TYPES.SENTIMENT_SATISFIED]: (
        <IconSentimentSatisfied key="sentiment_satisfied" />
    ),
    [MODE_TYPES.BUSINESS_CENTER]: <IconBusinessCenter key="business_center" />,
    [MODE_TYPES.CAKE]: <IconCake key="cake" />,
    [MODE_TYPES.CELEBRATION]: <IconCelebration key="celebration" />,
    [MODE_TYPES.CRUELTY_FREE]: <IconCrueltyFree key="cruelty_free" />,
    [MODE_TYPES.DIRECTION_CAR]: <IconDirectionsCar key="direction_car" />,
    [MODE_TYPES.FLAG]: <IconFlag key="flag" />,
    [MODE_TYPES.ICECREAM]: <IconIcecream key="icecream" />,
    [MODE_TYPES.LOOKS]: <IconLooks key="looks" />,
    [MODE_TYPES.PALETTE]: <IconPalette key="palette" />,
    [MODE_TYPES.REDEEM]: <IconRedeem key="redeem" />,
    [MODE_TYPES.SAILING]: <IconSailing key="sailing" />,
    [MODE_TYPES.TSUNAMI]: <IconTsunami key="tsunami" />,
});

const OPERATION_MODE_COLORS = {
    BLACK: "#000000",
    ORANGE: "#f3681a",
    GRAY: "#A3A3A3",
    BLUE: "#326FD1",
};

export const OPERATION_MODE_ICON_COLORS = {
    [OPERATION_MODES.HEATING]: {
        primary: OPERATION_MODE_COLORS.ORANGE,
        secondary: OPERATION_MODE_COLORS.ORANGE,
    },
    [OPERATION_MODES.COOLING]: {
        primary: OPERATION_MODE_COLORS.BLUE,
        secondary: OPERATION_MODE_COLORS.BLUE,
    },
    [OPERATION_MODES.VENTILATION]: {
        primary: OPERATION_MODE_COLORS.BLACK,
        secondary: OPERATION_MODE_COLORS.BLACK,
    },
    [OPERATION_MODES.OFF]: {
        primary: OPERATION_MODE_COLORS.GRAY,
        secondary: OPERATION_MODE_COLORS.GRAY,
    },
    [OPERATION_MODES.ON]: undefined, // icon should not be shown
    [OPERATION_MODES.CONTROL_ONLY]: undefined, // icon should not be shown
};

export const ENERGY_UNIT = Object.freeze({
    KILO: "KILO",
    MEGA: "MEGA",
});
